import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react'
import { QueueTrack, PlayerContextValue, RepeatMode, PlayHistoryItem } from '../types/player'
import { Release } from '../types/release'
import { track } from '../utils/track'

const PlayerContext = createContext<PlayerContextValue | undefined>(undefined)

const QUEUE_STORAGE_KEY = 'stonks-player-queue'
const VOLUME_STORAGE_KEY = 'stonks-player-volume'
const HISTORY_STORAGE_KEY = 'stonks-player-history'
const AUTO_SKIP_STORAGE_KEY = 'stonks-player-autoskip'

// Helper: Extract YouTube video ID from URL
function extractVideoId(url: string): string | null {
  if (!url) return null
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
  return match ? match[1] : null
}

// Helper: Get artist name from release
function getArtistName(release: Release): string {
  if (typeof release.artists === 'string') return release.artists
  if (Array.isArray(release.artists) && release.artists.length > 0) {
    return release.artists.map((a: any) => a.name || a).join(', ')
  }
  return 'Unknown Artist'
}

// Helper: Create QueueTrack from Release and Video
function createQueueTrack(release: Release, videoIndex: number): QueueTrack | null {
  if (!release.videos || !release.videos[videoIndex]) return null

  const video = release.videos[videoIndex]
  const videoUrl = video.src || video.uri || ''
  const videoId = extractVideoId(videoUrl)
  if (!videoId) return null

  const thumbnail = release.images?.[0]?.uri150 || release.images?.[0]?.uri || null

  const artists = Array.isArray(release.artists) ? release.artists : []
  const artistId = artists.length > 0 && typeof artists[0] === 'object' ? artists[0]?.id : undefined
  const labels = (release as any).labels
  const labelName = Array.isArray(labels) && labels.length > 0 ? (labels[0]?.name || labels[0]) : undefined

  return {
    id: `${release.id}-${videoIndex}-${Date.now()}`,
    releaseId: release.id,
    videoId,
    videoUrl,
    title: video.title,
    releaseTitle: release.title,
    artist: getArtistName(release),
    artistId,
    labelName,
    duration: video.duration,
    thumbnail,
  }
}

function clampRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0
  return Math.max(0, Math.min(1, ratio))
}

interface StoredState {
  queue: QueueTrack[]
  currentIndex: number
  repeatMode: RepeatMode
  shuffle: boolean
  radioSeedId: number | null
}

function getInitialState(): StoredState {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        queue: parsed.queue || [],
        currentIndex: parsed.currentIndex || 0,
        repeatMode: parsed.repeatMode || 'none',
        shuffle: parsed.shuffle || false,
        radioSeedId: parsed.radioSeedId ?? null,
      }
    }
  } catch (e) {
    console.error('Failed to parse player state from localStorage:', e)
  }
  return { queue: [], currentIndex: 0, repeatMode: 'none', shuffle: false, radioSeedId: null }
}

function getInitialVolume(): number {
  try {
    const stored = localStorage.getItem(VOLUME_STORAGE_KEY)
    if (stored) return parseInt(stored, 10) || 80
  } catch (e) {
    // Ignore
  }
  return 80
}

function getInitialHistory(): PlayHistoryItem[] {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return Array.isArray(parsed) ? parsed.slice(0, 100) : [] // Keep last 100
    }
  } catch (e) {
    console.error('Failed to parse history from localStorage:', e)
  }
  return []
}

function getInitialAutoSkip(): boolean {
  try {
    const stored = localStorage.getItem(AUTO_SKIP_STORAGE_KEY)
    if (stored) return stored === 'true'
  } catch (e) {
    // Ignore
  }
  return false
}

interface PlayerProviderProps {
  children: ReactNode
}

export function PlayerProvider({ children }: PlayerProviderProps) {
  const initialState = getInitialState()

  const [queue, setQueue] = useState<QueueTrack[]>(initialState.queue)
  const [currentIndex, setCurrentIndex] = useState(initialState.currentIndex)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolumeState] = useState(getInitialVolume)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isQueueOpen, setIsQueueOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [repeatMode, setRepeatMode] = useState<RepeatMode>(initialState.repeatMode)
  const [shuffle, setShuffle] = useState(initialState.shuffle)
  const [autoSkip30, setAutoSkip30] = useState(getInitialAutoSkip)
  const [playHistory, setPlayHistory] = useState<PlayHistoryItem[]>(getInitialHistory)
  const [radioSeedId, setRadioSeedId] = useState<number | null>(initialState.radioSeedId)

  const currentTrack = queue[currentIndex] || null
  const playerSeekRef = useRef<((seconds: number) => void) | null>(null)
  const pendingSeekRatioRef = useRef<number | null>(null)

  // Persist queue state to localStorage
  useEffect(() => {
    const state: StoredState = { queue, currentIndex, repeatMode, shuffle, radioSeedId }
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(state))
  }, [queue, currentIndex, repeatMode, shuffle, radioSeedId])

  // Persist volume separately
  useEffect(() => {
    localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString())
  }, [volume])

  // Persist history
  useEffect(() => {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(playHistory))
  }, [playHistory])

  // Persist auto-skip setting
  useEffect(() => {
    localStorage.setItem(AUTO_SKIP_STORAGE_KEY, autoSkip30.toString())
  }, [autoSkip30])

  // Queue management
  const addToQueue = useCallback((release: Release, videoIndex = 0): boolean => {
    const track = createQueueTrack(release, videoIndex)
    if (!track) return false

    setQueue((prev) => [...prev, track])
    return true
  }, [])

  const addAllVideosToQueue = useCallback((release: Release): number => {
    if (!release.videos || release.videos.length === 0) return 0

    const tracks = release.videos
      .map((_, index) => createQueueTrack(release, index))
      .filter((track): track is QueueTrack => track !== null)

    if (tracks.length === 0) return 0

    setQueue((prev) => [...prev, ...tracks])
    return tracks.length
  }, [])

  const removeFromQueue = useCallback((trackId: string) => {
    setQueue((prev) => {
      const index = prev.findIndex((t) => t.id === trackId)
      if (index === -1) return prev

      const newQueue = prev.filter((t) => t.id !== trackId)

      // Adjust currentIndex if needed
      if (index < currentIndex) {
        setCurrentIndex((curr) => curr - 1)
      } else if (index === currentIndex && newQueue.length > 0) {
        if (currentIndex >= newQueue.length) {
          setCurrentIndex(newQueue.length - 1)
        }
      }

      return newQueue
    })
  }, [currentIndex])

  const clearQueue = useCallback(() => {
    setQueue([])
    setCurrentIndex(0)
    setIsPlaying(false)
    setProgress(0)
    setDuration(0)
    setRadioSeedId(null)
  }, [])

  const playRelease = useCallback((release: Release, startIndex = 0, startRatio = 0): number => {
    if (!release.videos || release.videos.length === 0) return 0

    const playableVideos = release.videos
      .map((_, videoIndex) => ({ videoIndex, track: createQueueTrack(release, videoIndex) }))
      .filter((entry): entry is { videoIndex: number; track: QueueTrack } => entry.track !== null)

    const queueStartIndex = playableVideos.findIndex(entry => entry.videoIndex === startIndex)
    if (playableVideos.length === 0 || queueStartIndex === -1) return 0

    const tracks = playableVideos.map(entry => entry.track)

    const ratio = clampRatio(startRatio)
    const startSeconds = (tracks[queueStartIndex].duration || 0) * ratio

    pendingSeekRatioRef.current = ratio > 0 ? ratio : null
    setQueue(tracks)
    setCurrentIndex(queueStartIndex)
    setIsPlaying(true)
    setDuration(tracks[queueStartIndex].duration || 0)
    setProgress(startSeconds)
    setRadioSeedId(null)
    track('play', { releaseId: release.id })

    return tracks.length
  }, [])

  // Playback controls
  const play = useCallback(() => setIsPlaying(true), [])
  const pause = useCallback(() => setIsPlaying(false), [])
  const togglePlayPause = useCallback(() => setIsPlaying((prev) => !prev), [])

  const getNextIndex = useCallback((): number => {
    if (queue.length === 0) return -1
    if (repeatMode === 'one') return currentIndex

    if (shuffle) {
      if (queue.length === 1) return 0
      let nextIndex
      do {
        nextIndex = Math.floor(Math.random() * queue.length)
      } while (nextIndex === currentIndex)
      return nextIndex
    }

    const nextIndex = currentIndex + 1
    if (nextIndex >= queue.length) {
      return repeatMode === 'all' ? 0 : -1
    }
    return nextIndex
  }, [queue.length, currentIndex, repeatMode, shuffle])

  const next = useCallback(() => {
    const nextIndex = getNextIndex()
    if (nextIndex >= 0) {
      setCurrentIndex(nextIndex)
      setProgress(0)
    } else {
      setIsPlaying(false)
    }
  }, [getNextIndex])

  const previous = useCallback(() => {
    // If more than 3 seconds in, restart current track
    if (progress > 3) {
      setProgress(0)
      return
    }

    const prevIndex = currentIndex - 1
    if (prevIndex >= 0) {
      setCurrentIndex(prevIndex)
      setProgress(0)
    } else if (repeatMode === 'all') {
      setCurrentIndex(queue.length - 1)
      setProgress(0)
    }
  }, [currentIndex, progress, repeatMode, queue.length])

  const registerPlayerSeek = useCallback((fn: ((seconds: number) => void) | null) => {
    playerSeekRef.current = fn
  }, [])

  const seekTo = useCallback((seconds: number) => {
    setProgress(seconds)
    playerSeekRef.current?.(seconds)
  }, [])

  const setPendingSeekRatio = useCallback((ratio: number | null) => {
    pendingSeekRatioRef.current = ratio
  }, [])

  const peekPendingSeekRatio = useCallback((): number | null => {
    return pendingSeekRatioRef.current
  }, [])

  const consumePendingSeekRatio = useCallback((): number | null => {
    const r = pendingSeekRatioRef.current
    pendingSeekRatioRef.current = null
    return r
  }, [])

  const setVolume = useCallback((vol: number) => {
    setVolumeState(Math.max(0, Math.min(100, vol)))
  }, [])

  // Track selection
  const playTrack = useCallback((trackId: string) => {
    const index = queue.findIndex((t) => t.id === trackId)
    if (index >= 0) {
      setCurrentIndex(index)
      setIsPlaying(true)
      setProgress(0)
      track('play', { releaseId: queue[index]?.releaseId })
    }
  }, [queue])

  const playFromIndex = useCallback((index: number) => {
    if (index >= 0 && index < queue.length) {
      setCurrentIndex(index)
      setIsPlaying(true)
      setProgress(0)
      track('play', { releaseId: queue[index]?.releaseId })
    }
  }, [queue])

  // Radio: replace the queue with the seed release's tracks and mark it as the
  // radio seed. The RadioController tops the queue up with audio-similar
  // releases as it drains, producing an endless, taste-based station.
  const startRadio = useCallback((release: Release): number => {
    if (!release.videos || release.videos.length === 0) return 0
    const tracks = release.videos
      .map((_, index) => createQueueTrack(release, index))
      .filter((track): track is QueueTrack => track !== null)
    if (tracks.length === 0) return 0
    setQueue(tracks)
    setCurrentIndex(0)
    setProgress(0)
    setDuration(0)
    setIsPlaying(true)
    setRadioSeedId(release.id)
    track('play', { releaseId: release.id, radio: true })
    return tracks.length
  }, [])

  const stopRadio = useCallback(() => setRadioSeedId(null), [])

  // Toggle radio from the player: seed it with the current track's release and
  // let the RadioController extend the existing queue with similar records.
  const toggleRadio = useCallback(() => {
    setRadioSeedId(prev => (prev != null ? null : (currentTrack?.releaseId ?? null)))
  }, [currentTrack])

  // UI controls
  const toggleQueue = useCallback(() => setIsQueueOpen((prev) => !prev), [])
  const openQueue = useCallback(() => setIsQueueOpen(true), [])
  const closeQueue = useCallback(() => setIsQueueOpen(false), [])
  const toggleHistory = useCallback(() => setIsHistoryOpen((prev) => !prev), [])
  const openHistory = useCallback(() => setIsHistoryOpen(true), [])
  const closeHistory = useCallback(() => setIsHistoryOpen(false), [])

  // Mode controls
  const toggleRepeat = useCallback(() => {
    setRepeatMode((prev) => {
      if (prev === 'none') return 'all'
      if (prev === 'all') return 'one'
      return 'none'
    })
  }, [])

  const toggleShuffle = useCallback(() => setShuffle((prev) => !prev), [])

  const toggleAutoSkip30 = useCallback(() => setAutoSkip30((prev) => !prev), [])

  // History management
  const addToHistory = useCallback((track: QueueTrack) => {
    const historyItem: PlayHistoryItem = {
      ...track,
      playedAt: new Date().toISOString(),
    }
    setPlayHistory((prev) => [historyItem, ...prev].slice(0, 100)) // Keep last 100
  }, [])

  const clearHistory = useCallback(() => {
    setPlayHistory([])
  }, [])

  // State checks
  const isInQueue = useCallback(
    (releaseId: number): boolean => {
      return queue.some((t) => t.releaseId === releaseId)
    },
    [queue]
  )

  const hasVideos = useCallback((release: Release): boolean => {
    if (!release.videos || release.videos.length === 0) return false
    return release.videos.some((v) => extractVideoId(v.src || v.uri || '') !== null)
  }, [])

  const value = useMemo<PlayerContextValue>(() => ({
    // State
    isPlaying,
    currentTrack,
    currentIndex,
    queue,
    volume,
    progress,
    duration,
    isQueueOpen,
    isHistoryOpen,
    repeatMode,
    shuffle,
    autoSkip30,
    playHistory,
    radioSeedId,
    // Queue management
    addToQueue,
    addAllVideosToQueue,
    removeFromQueue,
    clearQueue,
    playRelease,
    // Playback controls
    play,
    pause,
    togglePlayPause,
    next,
    previous,
    seekTo,
    setVolume,
    setProgress,
    setDuration,
    // Track selection
    playTrack,
    playFromIndex,
    // Radio
    startRadio,
    stopRadio,
    toggleRadio,
    // UI controls
    toggleQueue,
    openQueue,
    closeQueue,
    toggleHistory,
    openHistory,
    closeHistory,
    // Mode controls
    toggleRepeat,
    toggleShuffle,
    toggleAutoSkip30,
    // History
    clearHistory,
    addToHistory,
    // State checks
    isInQueue,
    hasVideos,
    // Seek wiring
    registerPlayerSeek,
    setPendingSeekRatio,
    peekPendingSeekRatio,
    consumePendingSeekRatio,
  }), [
    isPlaying, currentTrack, currentIndex, queue, volume, progress, duration,
    isQueueOpen, isHistoryOpen, repeatMode, shuffle, autoSkip30, playHistory, radioSeedId,
    addToQueue, addAllVideosToQueue, removeFromQueue, clearQueue, playRelease,
    play, pause, togglePlayPause, next, previous, seekTo, setVolume, setProgress, setDuration,
    playTrack, playFromIndex, startRadio, stopRadio, toggleRadio,
    toggleQueue, openQueue, closeQueue, toggleHistory, openHistory, closeHistory,
    toggleRepeat, toggleShuffle, toggleAutoSkip30,
    clearHistory, addToHistory, isInQueue, hasVideos,
    registerPlayerSeek, setPendingSeekRatio, peekPendingSeekRatio, consumePendingSeekRatio,
  ])

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  )
}

export function usePlayer() {
  const context = useContext(PlayerContext)
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider')
  }
  return context
}
